import React, { useEffect, useMemo, useState } from "react";
import { listSchools, LOGO_STATUS } from "../services/schoolsService";
import "./school-picker.css";

/**
 * The school is chosen here, at purchase time — it is not a separate product.
 *
 * CUSTOM_SCHOOL is what lets the store launch before every school is
 * onboarded: the buyer picks "not listed", checkout succeeds, and the order
 * is flagged so artwork is collected afterwards.
 */
export const CUSTOM_SCHOOL = {
  id: "__custom__",
  name: "My school isn't listed",
  state: "",
  logoStatus: LOGO_STATUS.PENDING,
  isCustom: true,
};

let schoolCache = null;

export default function SchoolPicker({
  value,
  onChange,
  defaultSchool = null,
  compact = false,
}) {
  const [schools, setSchools] = useState(schoolCache || []);
  const [loading, setLoading] = useState(!schoolCache);
  const [stateFilter, setStateFilter] = useState(
    defaultSchool?.state || value?.state || "all"
  );

  useEffect(() => {
    if (schoolCache) return;
    listSchools({ approvedOnly: true })
      .then((rows) => {
        schoolCache = rows;
        setSchools(rows);
      })
      .catch((err) => console.error("Could not load schools:", err))
      .finally(() => setLoading(false));
  }, []);

  // Pre-select the buyer's own school when we know it.
  useEffect(() => {
    if (!value && defaultSchool) onChange(defaultSchool);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSchool]);

  const states = useMemo(
    () => [...new Set(schools.map((s) => s.state).filter(Boolean))].sort(),
    [schools]
  );

  const visible = useMemo(
    () =>
      stateFilter === "all"
        ? schools
        : schools.filter((s) => s.state === stateFilter),
    [schools, stateFilter]
  );

  const handleSelect = (schoolId) => {
    if (schoolId === CUSTOM_SCHOOL.id) return onChange(CUSTOM_SCHOOL);
    if (!schoolId) return onChange(null);
    onChange(schools.find((s) => s.id === schoolId) || null);
  };

  if (loading) {
    return <div className="sp-loading">Loading schools…</div>;
  }

  return (
    <div className={`sp-wrap ${compact ? "sp-compact" : ""}`}>
      <label className="sp-label">School</label>

      <div className="sp-row">
        <select
          className="sp-state"
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          aria-label="Filter schools by state"
        >
          <option value="all">All states</option>
          {states.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <select
          className="sp-school"
          value={value?.id || ""}
          onChange={(e) => handleSelect(e.target.value)}
          aria-label="Choose a school"
        >
          <option value="">Choose a school…</option>
          {visible.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.district ? ` — ${s.district}` : ""}
              {s.logoStatus === LOGO_STATUS.PENDING ? " (logo needed)" : ""}
            </option>
          ))}
          <option value={CUSTOM_SCHOOL.id}>
            — My school isn't listed —
          </option>
        </select>
      </div>

      {value && (
        <div className="sp-selected">
          {value.logoUrl ? (
            <img className="sp-logo" src={value.logoUrl} alt={`${value.name} logo`} />
          ) : (
            <div className="sp-logo sp-logo-empty" aria-hidden="true">
              ?
            </div>
          )}

          <div className="sp-selected-text">
            <strong>{value.name}</strong>
            {value.state && <span className="sp-meta">{value.state}</span>}
            {value.mascot && <span className="sp-meta">{value.mascot}</span>}

            {value.logoStatus === LOGO_STATUS.PENDING && (
              <p className="sp-pending">
                We don't have this logo on file yet. You can still order — after
                checkout we'll ask you to upload it, and production starts once
                it arrives.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
